import { SpaEntityConfigWizard } from '@common/components/SpaEntity/SpaEntityConfigWizard';
import {
  SpaEntityConfigWizardProps,
  SpaEntityConfigWizardReduxProps,
} from '@common/components/SpaEntity/SpaEntityConfigWizard/SpaEntityConfigWizard.types';
import { Routes } from '@common/constants';
import {
  useGetSpaSettings,
  useGetSpaTreatment,
  useGetSpaTreatmentTimeslots,
} from '@features/spa/hooks/index';
import { useQueryClient } from '@tanstack/react-query';
import { parseDateToString } from '@utils/helpers';
import moment from 'moment';
import { FC, useEffect, useState } from 'react';
import { useRouter } from 'shared-routing';
import { SpaTreatmentFormStepEnum } from '../SpaTreatment.types';
import { SpaTreatmentFormStepsContainer } from '../SpaTreatmentFormStepsContainer';

export const SpaTreatmentConfigWizard: FC<
  SpaEntityConfigWizardProps & SpaEntityConfigWizardReduxProps
> = ({
  spaEntity,
  property,
  language,
  basketDetails,
  suggestions,
  bookingStep,
  selectedTimeSlot,
  isSpinnerVisible,
  existingGuests = [],
  signIn,
  destroyForm,
  handleBasketUpdate,
  journeyParams,
}) => {
  const router = useRouter();
  const queryClient = useQueryClient();
  const { data: spaTreatmentWithAvailabilityDetail, isFetching } = useGetSpaTreatment(
    property.id,
    language,
    spaEntity.id,
    selectedTimeSlot?.value || parseDateToString(journeyParams.spaDate)
  );
  const isExtrasAvailable = spaEntity?.extras?.length;
  const [loginError, setLoginError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfirmationVisible, setIsConfirmationVisible] = useState(false);
  const [isSignInModalOpen, setIsSignInModalOpen] = useState(false);
  const [startDate, setStartDate] = useState(
    parseDateToString(journeyParams.spaDate || journeyParams.fromDate)
  );

  const { data: timeslots, isFetching: isFetchingTimeslots } = useGetSpaTreatmentTimeslots(
    property.id,
    Number(spaEntity.id),
    startDate,
    language
  );

  const { data: spaSettings } = useGetSpaSettings(property.id);
  const { maxDaysAdvanceTreatments } = spaSettings || {};

  const daysToFetch = new Array(7).fill('').map((_, index) => {
    return moment(selectedTimeSlot?.startDateTime || parseDateToString(journeyParams?.spaDate))
      .add(index, 'days')
      .format('YYYY-MM-DD');
  });

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    if (isModalOpen) {
      setIsModalOpen(false);
      destroyForm('spaEntityConfigurationForm');
    }
  };

  useEffect(() => {
    if (bookingStep) {
      openModal();
    } else {
      closeModal();
    }
  }, [bookingStep]);

  const handleSignIn = async (data) => {
    try {
      await signIn(property.id, data);
      setIsSignInModalOpen(false);
    } catch (e) {
      setLoginError('invalidLogin');
    }
  };
  const handleSignInModalClose = () => {
    setIsSignInModalOpen(false);
  };

  const gotoEntityDetailPage = (): Promise<boolean> =>
    router.push({
      pathname: Routes.SPA_TREATMENT_DETAIL,
      query: {
        id: spaEntity.id,
      },
    });

  const goToStep = (bookingStep: string): Promise<boolean> =>
    router.push({
      pathname: Routes.SPA_TREATMENT_BOOKING,
      query: {
        id: spaEntity.id,
        bookingStep,
      },
    });

  const goToNextStep = async () => {
    if (bookingStep === SpaTreatmentFormStepEnum.AVAILABILITY && isExtrasAvailable) {
      goToStep(SpaTreatmentFormStepEnum.CUSTOMISE);
    }
  };

  const goToPreviousStep = () => {
    router.back();
  };

  const showConfirmation = () => {
    setIsConfirmationVisible(true);
  };

  const hideConfirmation = () => {
    setIsConfirmationVisible(false);
  };

  const finishWizardAction = async (data) => {
    await handleBasketUpdate(data);
    showConfirmation();
    await gotoEntityDetailPage();
    await queryClient.invalidateQueries({
      predicate: (query) =>
        query.queryKey.includes('spaTreatment') && query.queryKey.includes('timeslots'),
    });
  };

  const hideArrivalTimes = spaSettings?.hideArrivalTimes || false;

  return (
    <SpaEntityConfigWizard
      isConfirmationVisible={isConfirmationVisible}
      basketDetails={basketDetails}
      hideConfirmation={hideConfirmation}
      suggestions={suggestions}
      isSignInModalOpen={isSignInModalOpen}
      handleSignInModalClose={handleSignInModalClose}
      spaEntity={spaTreatmentWithAvailabilityDetail || spaEntity}
      availableTimeslots={timeslots}
      isSpaEntityFetching={isFetchingTimeslots || isFetching}
      fetchingDates={isFetchingTimeslots ? daysToFetch : []}
      handleSignIn={handleSignIn}
      loginError={loginError}
      isModalOpen={isModalOpen}
      isSpinnerVisible={isSpinnerVisible || isFetchingTimeslots || isFetching}
      goToPreviousStep={goToPreviousStep}
      gotoEntityDetailPage={gotoEntityDetailPage}
      SpaEntityFormSteps={SpaTreatmentFormStepsContainer}
      bookingStep={bookingStep}
      language={language}
      existingGuests={existingGuests}
      maxDaysAdvance={maxDaysAdvanceTreatments || 0}
      isExtrasAvailable={isExtrasAvailable}
      goToNextStep={goToNextStep}
      setIsSignInModalOpen={setIsSignInModalOpen}
      finishWizardAction={finishWizardAction}
      selectedTimeSlot={selectedTimeSlot}
      goToStep={goToStep}
      SpaEntityFormStepEnum={SpaTreatmentFormStepEnum}
      setStartDate={setStartDate}
      modulesExcludingDisabled={property.modulesExcludingDisabled}
      hideArrivalTimes={hideArrivalTimes}
    />
  );
};
